import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import axios, { CancelToken } from "axios";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { cloneDeep, size } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { StatusCode } from "api/protocols";
import {
  PostLeagueCompetitionFormat,
  TPostLeagueCompetitionFormat,
} from "api/rpc/2024-04/facilityAdmin/league/scoring/format";
import {
  GetLeagueFormatTemplate,
  PostLeagueFormatTemplate,
  TPostLeagueFormatTemplate,
} from "api/rpc/2024-04/facilityAdmin/league/scoring/formatTemplate";
import { GetLeagueDivision } from "api/rpc/2024-04/facilityAdmin/league/scoring/division";
import { GetSeasonLeaderboard } from "api/rpc/2024-04/facilityAdmin/league/scoring/leaderboard";

import { showError, showSuccess } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";
import {
  TCompetitionFormat,
  TCompetitionFormatHoles,
  TCompetitionHandicap,
  TCompetitionOrganization,
  TCompetitionScoreCombo,
  TCompetitionTeamScores,
  TLeagueDivision,
  TPlayerCompetition,
  TLeagueRoundFormat,
  TLeagueRoundFormatTemplate,
  TLeagueSeasonLeaderboard,
  TCompetitionFormatPointAllocation,
} from "redux/reducers/models/league";
import { handleChangeEventInput } from "helpers/Helpers";

import useLeagueScoringFormatOptions from "./useLeagueScoringFormatOptions";
import useModal from "hooks/modals/useModal";
import Page from "components/page/Page";
import Card from "components/card/Card";
import { Select } from "components/select";
import FormLayout from "components/form/FormLayout";
import { IPrimaryPageAction } from "components/page/PageActions";
import Input from "components/form/input";
import Portal from "elements/Portal";
import Sheet from "components/sheet/Sheet";
import Checkbox from "components/form/checkbox/Checkbox";
import { ButtonNew as Button } from "components/buttonNew/";
import DataTable from "components/dataTable/DataTable";
import Callout from "components/callout/Callout";
import Popup from "components/popup/Popup";

import "./leagueScoringFormatNew.scss";

export default function LeagueScoringFormatNew() {
  const history = useHistory();
  const { t } = useTranslation();
  const { leagueId, scoresheetId } = useParams<{ leagueId: string; scoresheetId: string }>();

  const dispatch = useAppDispatch();

  const {
    organizationOptions,
    formatOptions,
    formatHoleOptions,
    handicapOptions,
    scoreComboOptions,
    teamScoreOptions,
    playerCompetitionOptions,
    nineHolePoints,
    eighteenHolePoints,
    pointAllocationOptions,
  } = useLeagueScoringFormatOptions();

  const [formatState, setFormatState] = useState<Omit<TPostLeagueCompetitionFormat, "league_scoresheet_id">>({
    league_division_id: undefined,
    title: "",
    organization: undefined,
    format: undefined,
    competition: undefined,
    default_participation_points: 0,
    holes: undefined,
    handicap: undefined,
    handicap_allowance: 100,
    team_scores_best_gross: 0,
    team_scores_best_net: 0,
    custom_points: nineHolePoints,
    leaderboard_data: [],
  });
  const [leaderboards, setLeaderboards] = useState<TLeagueSeasonLeaderboard[]>([]);
  const [filteredLeaderboards, setFilteredLeaderboards] = useState<TLeagueSeasonLeaderboard[]>([]);
  const [divisions, setDivisions] = useState<TLeagueDivision[]>([]);
  const [formatTemplates, setFormatTemplates] = useState<TLeagueRoundFormatTemplate[]>([]);

  const {
    state: loadFormatModal,
    updateModal: updateLoadFormatModal,
    closeModal: closeLoadFormatModal,
  } = useModal({
    selectedFormatId: null,
  });

  const {
    state: customPointsModal,
    updateModal: updateCustomPointsModal,
    closeModal: closeCustomPointsModal,
  } = useModal({
    selectedCustomPointsIndex: null,
    customPoints: null,
  });

  const {
    state: noLeaderboardPopup,
    updateModal: updateNoLeaderboardPopup,
    closeModal: closeNoLeaderboardPopup,
  } = useModal();

  const {
    state: newTemplateModal,
    updateModal: updateNewTemplateModal,
    closeModal: closeNewTemplateModal,
  } = useModal({
    name: "",
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadLeagueLeaderboards(leagueId, source.token);
    void loadLeagueDivision(leagueId, source.token);
    void loadLeagueFormatTemplates(leagueId, source.token);
    return () => source.cancel();
  }, [leagueId]);

  useEffect(() => {
    const filter = leaderboards
      ?.filter(leaderboard => leaderboard.league_division_id === formatState.league_division_id)
      ?.filter(leaderboard => leaderboard.organization === formatState.organization);
    setFilteredLeaderboards(filter);
  }, [formatState.organization, formatState.league_division_id]);

  async function loadLeagueLeaderboards(leagueId: string | number, token?: CancelToken) {
    const res = await GetSeasonLeaderboard({ league_id: leagueId }, token ? false : true, token);
    if (token && token.reason) {
      return;
    }

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading league leaderboards")));
      return;
    }

    setLeaderboards(res.data);
  }

  async function loadLeagueDivision(leagueId: string | number, token?: CancelToken) {
    const res = await GetLeagueDivision({ league_id: leagueId }, token ? false : true, token);
    if (token && token.reason) {
      return;
    }

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading league division")));
      return;
    }

    if (res.data.length > 0) {
      setFormatState(prev => ({ ...prev, league_division_id: res.data[0].id }));
    }

    setDivisions(res.data);
  }

  async function loadLeagueFormatTemplates(leagueId: string | number, token?: CancelToken) {
    const res = await GetLeagueFormatTemplate({ league_id: leagueId }, token ? false : true, token);

    if (token && token.reason) {
      return;
    }

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading league format templates")));
      return;
    }

    if (res.data) {
      setFormatTemplates(res.data);
    }
  }

  function formatScoringPoints() {
    let formattedScoringPoints = {};

    for (let i = 0; i < formatState.custom_points.length; i++) {
      formattedScoringPoints = {
        ...formattedScoringPoints,
        [formatState.custom_points[i].id]: formatState.custom_points[i].points,
      };
    }

    return formattedScoringPoints;
  }

  function formatLeaderboardData() {
    const tempLeaderboardData = [...formatState.leaderboard_data];

    for (let i = 0; i < tempLeaderboardData.length; i++) {
      if (tempLeaderboardData[i].custom_points) {
        let formattedCustomPoints = {};

        for (let j = 0; j < tempLeaderboardData[i].custom_points.length; j++) {
          formattedCustomPoints = {
            ...formattedCustomPoints,
            [tempLeaderboardData[i].custom_points[j].position]: tempLeaderboardData[i].custom_points[j].points,
          };
        }

        tempLeaderboardData[i].custom_points = formattedCustomPoints;
      }
    }

    return tempLeaderboardData;
  }

  async function saveScoringFormat() {
    const params: TPostLeagueCompetitionFormat = {
      league_scoresheet_id: scoresheetId,
      ...formatState,
      // No gross score if organization is individual
      team_scores_best_gross:
        formatState.organization === "individual" ? undefined : formatState.team_scores_best_gross,
      // No net score if organization is individual OR team_scoring is ALL
      team_scores_best_net: formatState.organization === "individual" ? undefined : formatState.team_scores_best_net,
      // No score combination if team_scoring is BEST
      team_scores_combination:
        formatState.team_scores === "best_scores" ? undefined : formatState.team_scores_combination,
      handicap_allowance: formatState.handicap_allowance / 100,
      custom_points: formatScoringPoints(),
      leaderboard_data: formatLeaderboardData(),
    };

    if (!params.league_division_id) {
      dispatch(showError(t("A league division is required to save a new scoring format.")));
      return;
    }

    const res = await PostLeagueCompetitionFormat(params, true);
    if (res.status !== StatusCode.OK) {
      dispatch(showError(typeof res.data === "string" ? res.data : t("Error creating new format.")));
      return;
    }

    dispatch(showSuccess(t("Successfully created new format.")));
    history.push(`/admin/league/${leagueId}/rounds/scoresheet/${scoresheetId}/overview`);
  }

  async function saveFormatAsTemplate() {
    const params: TPostLeagueFormatTemplate = {
      ...formatState,
      league_id: leagueId,
      name: newTemplateModal.name,
      // No gross score if organization is individual
      team_scores_best_gross:
        formatState.organization === "individual" ? undefined : formatState.team_scores_best_gross,
      // No net score if organization is individual OR team_scoring is ALL
      team_scores_best_net: formatState.organization === "individual" ? undefined : formatState.team_scores_best_net,
      // No score combination if team_scoring is BEST
      team_scores_combination:
        formatState.team_scores === "best_scores" ? undefined : formatState.team_scores_combination,
      handicap_allowance: formatState.handicap_allowance / 100,
      custom_points: formatScoringPoints(),
      leaderboard_data: formatLeaderboardData(),
    };

    if (!params.league_division_id) {
      dispatch(showError(t("A league division is required to save a format template.")));
      return;
    }

    const res = await PostLeagueFormatTemplate(params, true);
    if (res.status !== StatusCode.OK) {
      dispatch(showError(typeof res.data === "string" ? res.data : t("Error creating new template.")));
      return;
    }

    closeNewTemplateModal();
    dispatch(showSuccess(t("Successfully created new template.")));
  }

  function handleChangeScoringPoints(id: string, value: string) {
    const tempScoringPoints = formatState.custom_points;

    tempScoringPoints.find((point: any) => point.id === id).points = value;

    setFormatState(prevState => ({ ...prevState, custom_points: tempScoringPoints }));
  }

  function loadPreviousFormat(id: number) {
    const selectedFormat = formatTemplates.find(template => template.id === id);

    if (!selectedFormat) {
      dispatch(showError(t("No format template selected")));
      return;
    }

    const customPoints = JSON.parse(selectedFormat.custom_points);

    const formattedCustomPoints =
      selectedFormat.holes === "9_holes"
        ? [
            { id: "four_over", label: t("4 or More"), points: customPoints.four_over },
            { id: "three_over", label: "3", points: customPoints.three_over },
            { id: "two_over", label: "2", points: customPoints.two_over },
            { id: "one_over", label: "1", points: customPoints.one_over },
            { id: "even", label: t("Even"), points: customPoints.even },
            { id: "one_under", label: "-1", points: customPoints.one_under },
            { id: "two_under", label: "-2", points: customPoints.two_under },
            { id: "three_under", label: "-3", points: customPoints.three_under },
            { id: "four_under", label: "-4", points: customPoints.four_under },
            { id: "five_under", label: "-5", points: customPoints.five_under },
            { id: "six_under", label: "-6", points: customPoints.six_under },
            { id: "seven_under", label: t("-7 or Less"), points: customPoints.seven_under },
          ]
        : [
            { id: "eight_over", label: t("8 or More"), points: customPoints.eight_over },
            { id: "seven_over", label: "7", points: customPoints.seven_over },
            { id: "six_over", label: "6", points: customPoints.six_over },
            { id: "five_over", label: "5", points: customPoints.five_over },
            { id: "four_over", label: "4", points: customPoints.four_over },
            { id: "three_over", label: "3", points: customPoints.three_over },
            { id: "two_over", label: "2", points: customPoints.two_over },
            { id: "one_over", label: "1", points: customPoints.one_over },
            { id: "even", label: t("Even"), points: customPoints.even },
            { id: "one_under", label: "-1", points: customPoints.one_under },
            { id: "two_under", label: "-2", points: customPoints.two_under },
            { id: "three_under", label: "-3", points: customPoints.three_under },
            { id: "four_under", label: "-4", points: customPoints.four_under },
            { id: "five_under", label: "-5", points: customPoints.five_under },
            { id: "six_under", label: "-6", points: customPoints.six_under },
            { id: "seven_under", label: "-7", points: customPoints.seven_under },
            { id: "eight_under", label: "-8", points: customPoints.eight_under },
            { id: "nine_under", label: "-9", points: customPoints.nine_under },
            { id: "ten_under", label: t("-10 or Less"), points: customPoints.ten_under },
          ];

    setFormatState(prevState => ({
      ...prevState,
      league_division_id: selectedFormat.league_division_id,
      title: selectedFormat.title,
      organization: selectedFormat.organization,
      format: selectedFormat.format,
      competition: selectedFormat.competition,
      default_participation_points: selectedFormat.default_participation_points,
      holes: selectedFormat.holes,
      handicap: selectedFormat.handicap,
      handicap_allowance: selectedFormat.handicap_allowance * 100,
      team_scores: selectedFormat.team_scores,
      team_scores_best_gross: selectedFormat.team_scores_best_gross,
      team_scores_best_net: selectedFormat.team_scores_best_net,
      custom_points: formattedCustomPoints,
      leaderboard_data: JSON.parse(selectedFormat.leaderboard_data),
    }));
    updateLoadFormatModal({ isOpen: false });
    dispatch(showSuccess(t("Format template loaded successfully")));
  }

  function handleLeaderboardCheckboxChange(leaderboardId: number) {
    const tempLeaderboardData = formatState.leaderboard_data;
    const leaderboardIndex = tempLeaderboardData.findIndex(data => data.id === leaderboardId);

    if (leaderboardIndex < 0) {
      tempLeaderboardData.push({
        id: leaderboardId,
        point_allocation: formatState.format === "stroke_play" ? "posted" : "posted_points",
        custom_points: null,
      });
    } else {
      tempLeaderboardData.splice(leaderboardIndex, 1);
    }

    setFormatState(prevState => ({ ...prevState, leaderboard_data: tempLeaderboardData }));
  }

  function handlePointAllocationChange(value: TCompetitionFormatPointAllocation, leaderboardIndex: number) {
    const tempLeaderboardData = cloneDeep(formatState.leaderboard_data);

    tempLeaderboardData[leaderboardIndex].point_allocation = value;

    if (value === "custom") {
      tempLeaderboardData[leaderboardIndex].custom_points = [{ position: 1, points: 0 }];
    } else {
      tempLeaderboardData[leaderboardIndex].custom_points = null;
    }

    setFormatState(prevState => ({ ...prevState, leaderboard_data: tempLeaderboardData }));
  }

  function handleAddCustomPoint() {
    const tempCustomPoints = [...customPointsModal.customPoints];

    tempCustomPoints.push({ position: Number(tempCustomPoints[tempCustomPoints.length - 1].position) + 1, points: 0 });

    updateCustomPointsModal({ customPoints: tempCustomPoints });
  }

  function handleRemoveCustomPoint() {
    const tempCustomPoints = [...customPointsModal.customPoints];

    tempCustomPoints.splice(tempCustomPoints.length - 1, 1);

    updateCustomPointsModal({ customPoints: tempCustomPoints });
  }

  function handleEditCustomPoint(value: number | string, pointIndex: number) {
    const tempCustomPoints = [...customPointsModal.customPoints];

    tempCustomPoints[pointIndex].points = Number(value);

    updateCustomPointsModal({ customPoints: tempCustomPoints });
  }

  function handleSaveCustomPoints() {
    const tempLeaderboardData = cloneDeep(formatState.leaderboard_data);

    tempLeaderboardData[customPointsModal.selectedCustomPointsIndex].custom_points = customPointsModal.customPoints;

    setFormatState(prevState => ({ ...prevState, leaderboard_data: tempLeaderboardData }));
    updateCustomPointsModal({ isOpen: false, selectedCustomPointsIndex: null, customPoints: null });
  }

  function handleChangeHoles(value: "9_holes" | "18_holes") {
    if (value === formatState.holes) {
      return;
    }

    let formattedCustomPoints: any[] = [];

    if (formatState.custom_points) {
      formattedCustomPoints =
        value === "9_holes"
          ? formatState.holes === "18_holes"
            ? [...formatState.custom_points.splice(4, 12)]
            : [...formatState.custom_points]
          : formatState.holes === "9_holes"
          ? [
              { id: "eight_over", label: t("8 or More"), points: 0 },
              { id: "seven_over", label: "7", points: 0 },
              { id: "six_over", label: "6", points: 0 },
              { id: "five_over", label: "5", points: 0 },
              ...formatState.custom_points,
              { id: "eight_under", label: "-8", points: 0 },
              { id: "nine_under", label: "-9", points: 0 },
              { id: "ten_under", label: t("-10 or Less"), points: 0 },
            ]
          : [...formatState.custom_points];
    }

    setFormatState(prevState => ({
      ...prevState,
      custom_points: formattedCustomPoints,
      holes: value,
    }));
  }

  function checkForLeaderboards() {
    if (formatState?.leaderboard_data?.length <= 0 && formatState.format !== "skins") {
      updateNoLeaderboardPopup({ isOpen: true });
    } else {
      void saveScoringFormat();
    }
  }

  const primaryAction: IPrimaryPageAction = {
    action: () => void checkForLeaderboards(),
    content: t("Save"),
  };

  return (
    <Page
      title={t("New Scoring Format")}
      breadcrumbs={[
        {
          prefix: true,
          label: t("Round Overview"),
          url: `/admin/league/${leagueId}/rounds/scoresheet/${scoresheetId}/overview`,
        },
      ]}
      primaryAction={primaryAction}
      multipleActionDropdownAction={{
        label: t("Options"),
        dropdownProps: {
          alignment: "right",
          options: [
            {
              handler: () => updateLoadFormatModal({ isOpen: true }),
              label: t("Load Template"),
              disabled: formatTemplates?.length <= 0,
              type: "handler",
            },
            {
              handler: () => updateNewTemplateModal({ isOpen: true }),
              label: t("Save as Template"),
              type: "handler",
            },
          ],
        },
      }}
    >
      <Card>
        <Card.Section>
          <FormLayout>
            <FormLayout.Group>
              <Input
                type="text"
                label={t("Title")}
                id="title"
                value={formatState.title}
                onChange={e => handleChangeEventInput(e, formatState, setFormatState)}
              />
            </FormLayout.Group>
            {divisions.length >= 2 && (
              <FormLayout.Group>
                <Select
                  label={t("League Division")}
                  defaultValue={formatState.league_division_id}
                  onChange={(value: number) => setFormatState(prev => ({ ...prev, league_division_id: value }))}
                  disabled={divisions.length < 2}
                >
                  {divisions.map(division => (
                    <Select.Option key={division.id} value={division.id}>
                      {division.title}
                    </Select.Option>
                  ))}
                </Select>
              </FormLayout.Group>
            )}
          </FormLayout>
        </Card.Section>

        <Card.Section title={t("Individual or Team Scoring")}>
          <FormLayout>
            <FormLayout.Group>
              <Select
                defaultValue={formatState.organization}
                onChange={(value: TCompetitionOrganization) =>
                  setFormatState(prev => ({
                    ...prev,
                    organization: value,
                    team_scores: value === "individual" ? undefined : prev.team_scores,
                    team_scores_combination: value === "individual" ? undefined : prev.team_scores_combination,
                    team_scores_best_net: value === "individual" ? 0 : prev.team_scores_best_net,
                    team_scores_best_gross: value === "individual" ? 0 : prev.team_scores_best_gross,
                    competition: undefined,
                  }))
                }
              >
                {organizationOptions.map(option => (
                  <Select.Option key={option.id} value={option.id}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>
        <Card.Section title={t("Scoring")}>
          <FormLayout>
            <FormLayout.Group>
              <Select
                label={t("Scoring Format")}
                defaultValue={formatState.format}
                onChange={(value: TCompetitionFormat) => setFormatState(prev => ({ ...prev, format: value }))}
              >
                {formatOptions.map(option => (
                  <Select.Option key={option.id} value={option.id}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>

              <Select
                label={t("Competition")}
                defaultValue={formatState.competition}
                onChange={(value: TPlayerCompetition) => setFormatState(prev => ({ ...prev, competition: value }))}
                disabled={formatState.organization === undefined}
              >
                {playerCompetitionOptions
                  .filter(val => val.organization === formatState.organization)
                  .map(option => (
                    <Select.Option key={option.id} value={option.id}>
                      {option.label}
                    </Select.Option>
                  ))}
              </Select>
            </FormLayout.Group>
            <FormLayout.Group>
              <Select
                label={t("Holes")}
                defaultValue={formatState.holes}
                onChange={(value: TCompetitionFormatHoles) => handleChangeHoles(value)}
              >
                {formatHoleOptions.map(option => (
                  <Select.Option key={option.id} value={option.id}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>

              <Input
                label={t("Default Participation Points")}
                id="default_participation_points"
                type="number"
                value={formatState.default_participation_points}
                onChange={e => handleChangeEventInput(e, formatState, setFormatState)}
              />
            </FormLayout.Group>
            {formatState.format === "points_by_score" && (
              <div className="league-scoring-format-new-custom_points_container">
                {formatState.custom_points?.map((point: any) => {
                  return (
                    <div key={point.id} className="league-scoring-format-new-custom_points_input">
                      <Input
                        type="number"
                        label={point.label}
                        id={point.id}
                        value={point.points}
                        onChange={e => handleChangeScoringPoints(point.id, e.target.value)}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </FormLayout>
        </Card.Section>

        {formatState.organization === "team" ? (
          <Card.Section>
            <FormLayout>
              <FormLayout.Group>
                <Select
                  label={t("Team Scoring")}
                  defaultValue={formatState.team_scores}
                  onChange={(value: TCompetitionTeamScores) =>
                    setFormatState(prev => ({
                      ...prev,
                      team_scores: value,
                    }))
                  }
                >
                  {teamScoreOptions.map(option => (
                    <Select.Option key={option.id} value={option.id}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>

                {(formatState.team_scores === "best_scores" || formatState.team_scores === "gross_scores") && (
                  <Input
                    key={0}
                    label={t("Gross Scores")}
                    id="team_scores_best_gross"
                    type="number"
                    value={formatState.team_scores_best_gross}
                    onChange={e => handleChangeEventInput(e, formatState, setFormatState)}
                  />
                )}

                {(formatState.team_scores === "best_scores" || formatState.team_scores === "net_scores") && (
                  <Input
                    key={0}
                    label={t("Net Scores")}
                    id="team_scores_best_net"
                    type="number"
                    value={formatState.team_scores_best_net}
                    onChange={e => handleChangeEventInput(e, formatState, setFormatState)}
                  />
                )}
              </FormLayout.Group>
            </FormLayout>
          </Card.Section>
        ) : null}

        <Card.Section title={t("Handicaps")}>
          <FormLayout>
            <FormLayout.Group>
              <Select
                defaultValue={formatState.handicap}
                label={t("Handicap")}
                onChange={(value: TCompetitionHandicap) => setFormatState(prev => ({ ...prev, handicap: value }))}
              >
                {handicapOptions.map(option => (
                  <Select.Option key={option.id} value={option.id}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>

        {filteredLeaderboards?.length > 0 && formatState.organization && formatState.format !== "skins" ? (
          <Card.Section title={t("Leaderboards")}>
            <div className={"ui-checkbox-group formats-checkbox-group"}>
              {filteredLeaderboards?.map((leaderboard, index) => (
                <div key={index}>
                  <div
                    onClick={() => handleLeaderboardCheckboxChange(leaderboard.id)}
                    className={classNames("ui-checkbox-group-item", {
                      "ui-checkbox-group-item_selected":
                        formatState.leaderboard_data.findIndex(data => data.id === leaderboard.id) >= 0,
                    })}
                  >
                    <div className="ui-checkbox-group-item-content">
                      <p className="location-title">{leaderboard.title}</p>
                    </div>
                    <div className="pointer-events-none">
                      <Checkbox
                        key={index}
                        checked={formatState.leaderboard_data.findIndex(data => data.id === leaderboard.id) >= 0}
                        fullWidth
                        size="medium"
                        onChange={() => handleLeaderboardCheckboxChange(leaderboard.id)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card.Section>
        ) : (
          <Card.Section>
            <Callout
              type="info"
              title={t("No Leaderboards Available")}
              content={t("No leaderboards have been created for the selected division and organization")}
              actions={{
                primary: {
                  label: t("Leaderboards"),
                  onClick: () => history.push(`/admin/league/${leagueId}/scoring/leaderboards`),
                },
              }}
            />
          </Card.Section>
        )}
      </Card>

      <Portal isMounted={loadFormatModal.isOpen}>
        <Sheet
          open={loadFormatModal.isOpen}
          title={t("Load Format Template")}
          onCancel={closeLoadFormatModal}
          closable
          onOk={() => loadPreviousFormat(loadFormatModal.selectedFormatId)}
          size="small"
          overflow
        >
          <Select
            label={t("Format Template")}
            onChange={(value: number) => updateLoadFormatModal({ selectedFormatId: value })}
          >
            {formatTemplates?.map(format => (
              <Select.Option key={format.id} value={format.id}>
                {format.name}
              </Select.Option>
            ))}
          </Select>
        </Sheet>
      </Portal>

      <Portal isMounted={customPointsModal.isOpen}>
        <Sheet
          open={customPointsModal.isOpen}
          title={t("Custom Leaderboard Points")}
          okText={t("Save")}
          onCancel={closeCustomPointsModal}
          closable
          onOk={handleSaveCustomPoints}
          size="small"
        >
          <DataTable
            columns={[
              { label: t("Position") },
              { label: t("Points") },
            ]}
          >
            {customPointsModal.customPoints?.map((point: any, index: number) => (
              <tr key={point.position}>
                <td>{point.position}</td>
                <td>
                  <Input
                    labelHidden
                    id={"handicap_allowance"}
                    type="number"
                    value={point.points}
                    onChange={e => handleEditCustomPoint(e.target.value, index)}
                  />
                </td>
              </tr>
            ))}
            <tr>
              <td style={{ alignItems: "center" }}>
                <Button onClick={handleAddCustomPoint} type="tertiary" size="small">
                  <FontAwesomeIcon icon={["far", "plus"]} size={"2x"} />
                </Button>
              </td>
              <td style={{ alignItems: "center" }}>
                <Button
                  onClick={handleRemoveCustomPoint}
                  type="tertiary"
                  size="small"
                  disabled={customPointsModal.customPoints?.length <= 1}
                >
                  <FontAwesomeIcon icon={["far", "minus"]} size={"2x"} />
                </Button>
              </td>
            </tr>
          </DataTable>
        </Sheet>
      </Portal>

      <Portal isMounted={noLeaderboardPopup.isOpen}>
        <Popup
          open={noLeaderboardPopup.isOpen}
          title={t("No Leaderboards Selected")}
          description={t(
            "This format will not be counted towards any season leaderboards. Are you sure you want to continue?",
          )}
          type="warning"
          okText={t("Continue")}
          onOk={saveScoringFormat}
          onCancel={closeNoLeaderboardPopup}
        />
      </Portal>

      <Portal isMounted={newTemplateModal.isOpen}>
        <Sheet
          title={t("Save as Template")}
          open={newTemplateModal.isOpen}
          onOk={saveFormatAsTemplate}
          okText={t("Save")}
          onCancel={closeNewTemplateModal}
          size="small"
          overflow
        >
          <Input
            label={t("Template Name")}
            id={"name"}
            value={newTemplateModal.name}
            onChange={e => updateNewTemplateModal({ name: e.target.value })}
          />
        </Sheet>
      </Portal>
    </Page>
  );
}
